import React, { ErrorInfo } from 'react';

import { ErrorBoundaryProps, ErrorBoundaryState } from './ErrorBoundary.decl';

import { GenericErrorScreen } from '@components/GenericErrorScreen/GenericErrorScreen';
import { NotFoundErrorScreen } from '@components/NotFoundErrorScreen/NotFoundErrorScreen';
import { DashboardExtensionError, NotFoundError } from '@dashboard/library';
import * as datadogRumHelpers from '@helpers/datadogRum.helpers';
import { useLocation } from 'react-router';

function isDashboardExtensionError(error: Error): error is DashboardExtensionError {
  return error.name === 'DashboardExtensionError';
}

class BaseErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidUpdate(nextProps: ErrorBoundaryProps) {
    const { location } = this.props;
    const { error } = this.state;

    if (location !== nextProps.location && error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (error instanceof NotFoundError) {
      return;
    }

    if (isDashboardExtensionError(error)) {
      datadogRumHelpers.addReactError('ExtensionErrorBoudary', error, info, error.data);
      return;
    }

    datadogRumHelpers.addReactError('ErrorBoundary', error, info);
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (error instanceof NotFoundError) {
      return <NotFoundErrorScreen />;
    }

    if (error) {
      return <GenericErrorScreen />;
    }

    return children;
  }
}

export function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return <BaseErrorBoundary location={location}>{children}</BaseErrorBoundary>;
}
